import { z } from 'zod';
import {
  CargoExchangeRateSchema,
  CargoNonnegMoneySchema,
  CargoSignedMoney2dpSchema,
} from './article.js';
import { CargoPaymentCurrencySchema, CargoPaymentDocReviewStatusSchema } from './enums.js';

// Cargo client-payment INPUT + WIRE shapes (#1230): client tariff and
// payment-document attach/list. The download-url response reuses the storage
// schema, so it is not redefined here. Money figures cross the wire as `\d+\.\d{2}` strings
// (`CargoNonnegMoneySchema`) so the backend `Decimal(12,2)` precision survives
// JSON round-tripping.

// 25 MiB ceiling — mirrors the backend `STORAGE_SCOPE_LIMIT_BYTES['cargo-payment-doc']`.
const CARGO_PAYMENT_DOC_MAX_BYTES = 25 * 1024 * 1024;

/**
 * @aiPurpose `PATCH /cargo/:id/client-tariff` body (#1230). Mirrors the dirty
 *   free-text `transportationTariff` typing in `registry-io.ts`: a nullable
 *   string accepted verbatim (the migration tightens these later). `.strict()`
 *   so a stray key is a 400 instead of a silent no-op.
 */
export const CargoClientTariffInputSchema = z
  .object({
    transportationTariffForClients: z.string().nullable(),
  })
  .strict();
export type CargoClientTariffInput = z.infer<typeof CargoClientTariffInputSchema>;

/**
 * @aiPurpose `POST /cargo/:id/payment-documents` body (#1230). `objectKey`
 *   binds to an already-uploaded `cargo-payment-doc` storage object owned by
 *   the uploader (the scope/owner binding is enforced in the service, not
 *   here). `contentLength` is a positive int capped at the 25 MiB scope ceiling;
 *   `amount` is optional nonneg money («Деньги в платежки»). `.strict()`.
 */
export const CargoPaymentDocumentCreateInputSchema = z
  .object({
    objectKey: z.string().min(1),
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    contentLength: z.number().int().positive().max(CARGO_PAYMENT_DOC_MAX_BYTES),
    amount: CargoNonnegMoneySchema.nullable().optional(),
  })
  .strict();
export type CargoPaymentDocumentCreateInput = z.infer<typeof CargoPaymentDocumentCreateInputSchema>;

/**
 * @aiPurpose payment-document list/detail WIRE item (#1230). Deliberately omits
 *   the raw `objectKey` — clients download via the `:documentId/download-url`
 *   route, never by reconstructing the storage key. `uploadedBy` is a minimal
 *   id+name ref so the UI renders the uploader without a `GET /users` round-trip.
 * @aiSecurity NEVER add `objectKey` to this shape: it embeds the uploader's
 *   userId (PII) and is the storage-authorization surface. The list response is
 *   the only thing returned to the client.
 */
export const CargoPaymentDocumentWireSchema = z
  .object({
    id: z.string().uuid(),
    fileName: z.string(),
    contentType: z.string(),
    contentLength: z.number().int().nonnegative(),
    // `amount` is a nonneg payment value; `balance` is a running balance that
    // may legitimately be negative (backend serializes both via Decimal.toFixed(2)).
    amount: CargoNonnegMoneySchema.nullable(),
    balance: CargoSignedMoney2dpSchema.nullable(),
    uploadedBy: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    // WHY (#1933): populated by #1936's review flow; kept optional so pre-#1936
    // payloads (which omit the review fields entirely) still validate.
    reviewStatus: CargoPaymentDocReviewStatusSchema.optional(),
    currency: CargoPaymentCurrencySchema.nullable().optional(),
    foreignAmount: CargoNonnegMoneySchema.nullable().optional(),
    exchangeRate: CargoExchangeRateSchema.nullable().optional(),
    receivedRub: CargoNonnegMoneySchema.nullable().optional(),
  })
  .strict();
export type CargoPaymentDocumentWire = z.infer<typeof CargoPaymentDocumentWireSchema>;

/**
 * @aiPurpose `GET /cargo/:id/payment-documents` envelope (#1230): the active
 *   (non-deleted) payment documents for one visible cargo row.
 */
export const CargoPaymentDocumentListResponseSchema = z
  .object({
    documents: z.array(CargoPaymentDocumentWireSchema),
  })
  .strict();
export type CargoPaymentDocumentListResponse = z.infer<
  typeof CargoPaymentDocumentListResponseSchema
>;
